import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * Lightweight quick-add dialog used inside the Firmenakte.
 *
 * Props:
 *  - open / onOpenChange: dialog state
 *  - title: dialog title
 *  - resource: API path (e.g. "contacts")
 *  - fields: same shape as ResourcePage fields (text, email, tel, textarea, select, date, datetime, number)
 *  - defaults: initial values
 *  - lockedFields: keys that won't be rendered but ARE sent (e.g. { company_id })
 *  - onSuccess: callback (created) => void
 *  - invalidateQueryKeys: list of react-query keys to invalidate after success
 *  - testidPrefix: kebab-case prefix for data-testids
 */
export default function QuickAddDialog({
  open,
  onOpenChange,
  title,
  resource,
  fields,
  defaults = {},
  lockedFields = {},
  onSuccess,
  invalidateQueryKeys = [],
  testidPrefix,
  submitButtonLabel,
  successMessage,
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ ...defaults });
  const [submitting, setSubmitting] = useState(false);

  // Reset form when dialog opens
  function handleOpenChange(next) {
    if (next) setForm({ ...defaults });
    onOpenChange?.(next);
  }

  function setField(name, value) {
    setForm((s) => ({ ...s, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...lockedFields };
      fields.forEach((f) => {
        let v = form[f.name];
        if (v === "" || v === undefined) v = null;
        else if (f.type === "datetime" && v) v = new Date(v).toISOString();
        else if (f.type === "date" && v) v = new Date(v).toISOString();
        else if (f.type === "number" && v != null && v !== "") v = Number(v);
        payload[f.name] = v;
      });
      const { data } = await api.post(`/${resource}`, payload);
      toast.success(successMessage || "Gespeichert");
      invalidateQueryKeys.forEach((key) => qc.invalidateQueries({ queryKey: key }));
      onSuccess?.(data);
      onOpenChange?.(false);
      setForm({ ...defaults });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid={`${testidPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display">{title}</DialogTitle>
          <DialogDescription>Kurz ausfüllen und speichern.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.map((f) => {
              const val = form[f.name] ?? "";
              const fullSpan = f.fullWidth || f.type === "textarea";
              return (
                <div key={f.name} className={fullSpan ? "md:col-span-2" : ""}>
                  <Label htmlFor={`qa-${f.name}`} className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                    {f.label}
                    {f.required ? <span className="text-rose-500"> *</span> : null}
                  </Label>
                  {f.type === "textarea" ? (
                    <Textarea
                      id={`qa-${f.name}`}
                      value={val || ""}
                      onChange={(e) => setField(f.name, e.target.value)}
                      rows={f.rows || 3}
                      className="mt-1"
                      data-testid={`${testidPrefix}-field-${f.name}`}
                    />
                  ) : f.type === "select" ? (
                    <Select
                      value={val ? String(val) : ""}
                      onValueChange={(v) => setField(f.name, v)}
                    >
                      <SelectTrigger className="mt-1" data-testid={`${testidPrefix}-field-${f.name}`}>
                        <SelectValue placeholder={f.placeholder || "Auswählen"} />
                      </SelectTrigger>
                      <SelectContent>
                        {(f.options || []).map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id={`qa-${f.name}`}
                      type={f.type === "datetime" ? "datetime-local" : f.type === "date" ? "date" : f.type || "text"}
                      value={val || ""}
                      required={f.required}
                      step={f.step}
                      placeholder={f.placeholder}
                      onChange={(e) => setField(f.name, e.target.value)}
                      className="mt-1"
                      data-testid={`${testidPrefix}-field-${f.name}`}
                    />
                  )}
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)} data-testid={`${testidPrefix}-cancel-btn`}>
              Abbrechen
            </Button>
            <Button
              type="submit"
              className="bg-black text-white hover:bg-gray-800"
              disabled={submitting}
              data-testid={`${testidPrefix}-submit-btn`}
            >
              {submitting ? "Speichern…" : (submitButtonLabel || "Speichern")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
